<script setup lang="ts">
import Sortable from 'sortablejs'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { dropTag, suggestTags, type TagZone } from '@/todos/tags'
import TagChip from './TagChip.vue'

const { t } = useI18n()
const props = defineProps<{
  modelValue: string[]
  placeholder: string
  disabled?: boolean
  prefix?: string
  linkFor?: (value: string) => string | null
  // Known values offered while typing; without any there is no dropdown.
  suggestions?: string[]
  // Show the first value in a "category" slot and let the chips be dragged (onto the slot to make one the category).
  categorySlot?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [value: string[]] }>()

const slotChips = ref<HTMLElement | null>(null)
const otherChips = ref<HTMLElement | null>(null)
const instances: Sortable[] = []

// With the category slot the first value sits in it and the others follow; without it everything is one list.
const category = computed(() => (props.categorySlot ? props.modelValue.slice(0, 1) : []))
const others = computed(() => (props.categorySlot ? props.modelValue.slice(1) : props.modelValue))
const draggable = computed(() => props.categorySlot && !props.disabled && props.modelValue.length > 1)

function zoneOf(element: HTMLElement): TagZone {
  return element.dataset.zone === 'category' ? 'category' : 'tags'
}

function destroySortables(): void {
  for (const instance of instances.splice(0)) instance.destroy()
}

// Both lists are one Sortable group. The browser's DOM move is undone and the new order is emitted, Vue renders it.
watch(
  [slotChips, otherChips, draggable],
  ([slot, rest, enabled]) => {
    destroySortables()
    if (!slot || !rest || !enabled) return
    for (const element of [slot, rest]) {
      instances.push(
        Sortable.create(element, {
          group: 'tags',
          draggable: '.tag',
          animation: 150,
          delay: 150,
          delayOnTouchOnly: true,
          touchStartThreshold: 4,
          onEnd: (event) => {
            const { from, to, item, oldIndex, newIndex } = event
            if (oldIndex === undefined || newIndex === undefined) return
            to.removeChild(item)
            from.insertBefore(item, from.children[oldIndex] ?? null)
            if (from === to && oldIndex === newIndex) return
            emit(
              'update:modelValue',
              dropTag(props.modelValue, zoneOf(from), oldIndex, zoneOf(to), newIndex),
            )
          },
        }),
      )
    }
  },
  { flush: 'post' },
)

onBeforeUnmount(destroySortables)

const draft = ref('')
const focused = ref(false)
const active = ref(-1)

// Only the part after the last comma is being typed right now.
const typing = computed(() => draft.value.slice(draft.value.lastIndexOf(',') + 1))
const matches = computed(() =>
  props.suggestions?.length && !props.disabled
    ? suggestTags(props.suggestions, props.modelValue, typing.value)
    : [],
)
const open = computed(() => focused.value && matches.value.length > 0)

function add(): void {
  const values = draft.value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v && !props.modelValue.includes(v))
  draft.value = ''
  if (values.length) emit('update:modelValue', [...props.modelValue, ...values])
}

function pick(value: string): void {
  const done = draft.value.slice(0, draft.value.lastIndexOf(',') + 1)
  draft.value = done + value
  active.value = -1
  add()
}

function onEnter(): void {
  if (open.value && active.value >= 0) pick(matches.value[active.value])
  else add()
}

function move(step: number): void {
  if (!open.value) return
  const count = matches.value.length
  active.value = (active.value + step + count + (active.value < 0 && step < 0 ? 1 : 0)) % count
}

function onBlur(): void {
  focused.value = false
  active.value = -1
  add()
}

function remove(value: string): void {
  emit(
    'update:modelValue',
    props.modelValue.filter((v) => v !== value),
  )
}

function onBackspace(): void {
  if (!draft.value && props.modelValue.length) remove(props.modelValue[props.modelValue.length - 1])
}
</script>

<template>
  <div class="tags">
    <div v-if="categorySlot && modelValue.length" class="category-slot" :title="t('tags.dragHint')">
      <span class="slot-label">{{ t('tags.category') }}</span>
      <div ref="slotChips" class="chips slot-chips" data-zone="category">
        <TagChip
          v-for="value in category"
          :key="value"
          :value="value"
          :prefix="prefix"
          :link="linkFor?.(value)"
          :disabled="disabled"
          @remove="remove(value)"
        />
      </div>
    </div>
    <div ref="otherChips" class="chips" data-zone="tags">
      <TagChip
        v-for="value in others"
        :key="value"
        :value="value"
        :prefix="prefix"
        :link="linkFor?.(value)"
        :disabled="disabled"
        @remove="remove(value)"
      />
    </div>
    <input
      v-if="!disabled"
      v-model="draft"
      class="draft"
      :placeholder="modelValue.length ? '' : placeholder"
      enterkeyhint="done"
      @focus="focused = true"
      @input="active = -1"
      @keydown.enter.prevent="onEnter"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.esc="focused = false"
      @keydown.backspace="onBackspace"
      @blur="onBlur"
    />
    <ul v-if="open" class="suggestions" role="listbox">
      <!-- pointerdown.prevent keeps the input focused, otherwise its blur would commit the draft first -->
      <li
        v-for="(match, index) in matches"
        :key="match"
        role="option"
        :aria-selected="index === active"
        :class="{ active: index === active }"
        @pointerdown.prevent="pick(match)"
      >
        {{ prefix }}{{ match }}
      </li>
    </ul>
    <span v-else-if="!modelValue.length" class="ui-hint">—</span>
  </div>
</template>

<style scoped>
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
}

.category-slot {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.4rem;
  border: 1px dashed var(--tg-hint-color);
  border-radius: 0.6rem;
}

.slot-label {
  color: var(--tg-hint-color);
  font-size: 0.75rem;
}

.slot-chips {
  min-width: 2rem;
  min-height: 1.6rem;
}

.tags {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
}

.suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin: 0.2rem 0 0;
  padding: 0.2rem 0;
  list-style: none;
  border-radius: 0.5rem;
  background: var(--tg-secondary-bg-color, var(--tg-bg-color));
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.25);
}

.suggestions li {
  padding: 0.4rem 0.7rem;
  cursor: pointer;
}

.suggestions li.active {
  background: var(--tg-button-color);
  color: var(--tg-button-text-color);
}

.draft {
  flex: 1;
  min-width: 6rem;
  padding: 0.3rem 0;
  border: none;
  background: transparent;
  color: var(--tg-text-color);
  font: inherit;
  outline: none;
}
</style>
